if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span.product-form__add-button-text');
        this.defaultSubmitButtonText = (!this.submitButton.disabled && this.submitButtonText?.textContent.trim()) || window.variantStrings?.addToCart || 'Add to Bag';

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        
        // Listen for variant changes to re-check customization requirements
        this.addEventListener('change', this.handleVariantChange.bind(this));
        
        this.checkForCustomizedVariantOnLoad();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.dataset.nativeUnavailable === 'true') return;

        const isCustomized = this.isCustomizedVariant();
        const hasPersonalization = this.hasPersonalizationData();

        if (isCustomized && !hasPersonalization) {
          this.openPersonalizationModal();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        Promise.resolve(this.prepareConfiguredCart?.(formData) || [])
          .then((addons) => {
            if (addons.length) {
              config.headers['Content-Type'] = 'application/json';
              config.body = JSON.stringify(this.buildItemsPayload(formData, addons));
            } else {
              config.body = formData;
            }
            return fetch(`${routes.cart_add_url}`, config);
          })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
              }
              return this.revealPartialAdd(formData.get('id'), response.description);
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            this.handleErrorMessage(e.message);
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      async revealPartialAdd(variantId, message) {
        if (this.cart?.localName !== 'cart-drawer') return;
        const sectionIds = this.cart.getSectionsToRender().map((section) => section.id).join(',');
        const [cart, sections] = await Promise.all([
          fetch(`${routes.cart_url}.js`).then((response) => response.json()),
          fetch(`${window.location.pathname}?sections=${sectionIds}`).then((response) => response.json()),
        ]);
        const line = cart.items.findIndex((item) => item.variant_id === Number(variantId)) + 1;
        if (!line) return;
        this.cart.renderContents({ sections });
        const lineError = this.cart.querySelector(`#CartDrawer-LineItemError-${line} .cart-item__error-text`);
        lineError.textContent = message;
        lineError.scrollIntoView({ block: 'nearest' });
      }

      buildItemsPayload(formData, addons) {
        const properties = {};
        formData.forEach((value, key) => {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        });

        const item = { id: Number(formData.get('id')), quantity: Number(formData.get('quantity') || 1), properties };
        return {
          items: [item, ...addons],
          sections: formData.get('sections'),
          sections_url: formData.get('sections_url'),
        };
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = this.defaultSubmitButtonText;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      handleVariantChange(event) {
        if (event.target.name === 'id' || event.target.name?.includes('option')) {
          setTimeout(() => {
            this.performCustomizationCheck();
          }, 100);
        }
      }

      isCustomizedVariant() {
        const variantPicker = document.querySelector('variant-picker');
        
        if (variantPicker) {
          const selectedOptions = variantPicker.querySelectorAll('input[type="radio"]:checked');
          const hasCustomizedOption = Array.from(selectedOptions).some(input => input.value === 'Customized');
          
          if (hasCustomizedOption) {
            return true;
          }
        }
        
        const embroideryButton = document.querySelector('.embroidery-popup-modal__button');
        if (embroideryButton) {
          return true;
        }
        
        return false;
      }

      hasPersonalizationData() {
        const textInput = this.form.querySelector('[name="properties[Embroidery Text]"]');
        const fontInput = this.form.querySelector('[name="properties[Embroidery Font]"]');
        const colorInput = this.form.querySelector('[name="properties[Embroidery Thread Color]"]');
        
        const hasEmbroideryText = textInput?.value?.trim();
        const hasEmbroideryFont = fontInput?.value;
        const hasEmbroideryColor = colorInput?.value;

        return hasEmbroideryText && hasEmbroideryFont && hasEmbroideryColor;
      }

      openPersonalizationModal() {
        const embroideryButton = document.querySelector('.embroidery-popup-modal__button');
        
        if (embroideryButton) {
          embroideryButton.click();
        } 
      }

      checkForCustomizedVariantOnLoad() {
        this.performCustomizationCheck();
        
        setTimeout(() => {
          this.performCustomizationCheck();
        }, 500);
        
        setTimeout(() => {
          this.performCustomizationCheck();
        }, 1000);
      }

      performCustomizationCheck() {
        if (this.dataset.nativeUnavailable === 'true') {
          this.submitButton.setAttribute('disabled', 'disabled');
          return;
        }
        const isInProductCard = this.closest('.cart-pair-save__form') ||
                               this.closest('.product-card') ||
                               this.closest('.card-wrapper') ||
                               this.closest('.product-item') ||
                               this.closest('[class*="card"]') ||
                               this.closest('cart-product-recommendations');

        if (isInProductCard) {
          return;
        }
        
        const isCustomized = this.isCustomizedVariant();
        const hasPersonalization = this.hasPersonalizationData();
        
        if (isCustomized && !hasPersonalization) {
          this.showCustomizationRequiredMessage();
          this.submitButton.setAttribute('disabled', 'disabled');
          
          if(this.submitButtonText){ 
            this.submitButtonText.textContent = 'Personalize to Add to Bag';
          }
        } else if (!isCustomized) {
          this.hideCustomizationRequiredMessage();
          this.submitButton.removeAttribute('disabled');
          
          if(this.submitButtonText) {
            this.submitButtonText.textContent = this.defaultSubmitButtonText;
          }
        }
      }

      hideCustomizationRequiredMessage() {
        const existingMessage = document.querySelector('.customization-required-message');
        if (existingMessage) {
          existingMessage.remove();
        }
      }

      showCustomizationRequiredMessage() {
        const isInProductCard = this.closest('.product-card') || 
                               this.closest('.card-wrapper') || 
                               this.closest('.product-item') ||
                               this.closest('[class*="card"]');
        
        if (isInProductCard) {
          return;
        }
        
        const existingMessage = document.querySelector('.customization-required-message');
        if (existingMessage) {
          existingMessage.remove();
        }

        const messageEl = document.createElement('div');
        messageEl.className = 'customization-required-message alert alert--warning';
        messageEl.innerHTML = `
          <p class="body-xs">This variant requires personalization. Please click "Personalize it" to add your embroidery details before adding to cart.</p>
        `;

        this.submitButton.parentElement.insertBefore(messageEl, this.submitButton);
      }

    }
  );
}
